import React, { useMemo } from 'react';
import { VisualizationEvent } from '../types';
import { RotationAngle } from '../utils';

interface HeatmapPlotProps {
  events: VisualizationEvent[];
  rotation?: RotationAngle;
  scale?: number;
  colorBy?: 'team' | 'stat_type';
  teamColors?: Record<string, string>;
  statTypeColors?: Record<string, string>;
  gridResolution?: number; // Not used for smooth heatmap, kept for compatibility
  opacity?: number; // Opacity of heatmap (0-1, default: 0.6)
  radius?: number; // Influence radius for each point (default: 100)
  smoothness?: number; // Smoothness/blur factor (default: 3.0, higher = smoother)
  pointSelection?: ('source' | 'destination')[]; // Which point(s) to use from position_data
}

/**
 * Heatmap plot visualization component
 * Renders a smooth, continuous density heatmap using radial gradients and Gaussian blur
 */
const HeatmapPlot: React.FC<HeatmapPlotProps> = ({
  events,
  rotation = 0,
  scale = 1.0,
  colorBy = 'team',
  teamColors = {},
  statTypeColors = {},
  opacity = 0.6,
  radius = 100,
  smoothness = 3.0,
  pointSelection = ['source'],
}) => {
  // Default team colors
  const defaultTeamColors: Record<string, string> = {
    default: '#3B82F6', // Blue
  };

  // Get color for an event based on colorBy setting
  const getEventColor = (event: VisualizationEvent): string => {
    switch (colorBy) {
      case 'team':
        return teamColors[event.team_id] || defaultTeamColors.default;
      case 'stat_type':
        // Use team-specific color if set (key format: `${statTypeId}-${teamId}`)
        // Fall back to stat-type-only color, then default
        if (event.team_id) {
          const teamSpecificKey = `${event.stat_type_id}-${event.team_id}`;
          if (statTypeColors[teamSpecificKey]) {
            return statTypeColors[teamSpecificKey];
          }
        }
        return statTypeColors[event.stat_type_id] || defaultTeamColors.default;
      default:
        return defaultTeamColors.default;
    }
  };

  // Group events by color for multi-color heatmap
  const eventsByColor = useMemo(() => {
    const map = new Map<string, Array<{ x: number; y: number }>>();
    events.forEach((event) => {
      if (!event.position_data || event.position_data.length === 0) return;
      const color = getEventColor(event);
      if (!map.has(color)) {
        map.set(color, []);
      }
      
      // Determine which points to use based on pointSelection
      let pointsToUse: Array<{ x: number; y: number }> = [];
      
      const hasSource = pointSelection.includes('source');
      const hasDestination = pointSelection.includes('destination');
      
      if (hasSource && hasDestination) {
        // Use both first and last points (if different)
        const first = event.position_data[0];
        const last = event.position_data[event.position_data.length - 1];
        if (first.x !== last.x || first.y !== last.y) {
          pointsToUse = [first, last];
        } else {
          pointsToUse = [first];
        }
      } else if (hasSource) {
        // Use only the first point
        pointsToUse = [event.position_data[0]];
      } else if (hasDestination) {
        // Use only the last point
        pointsToUse = [event.position_data[event.position_data.length - 1]];
      } else {
        // Default to first point if nothing selected
        pointsToUse = [event.position_data[0]];
      }
      
      // Add all selected points to the heatmap
      pointsToUse.forEach((point) => {
        map.get(color)!.push({ x: point.x, y: point.y });
      });
    });
    return map;
  }, [events, colorBy, teamColors, statTypeColors, pointSelection]);

  // Create radial gradient ID for each color
  const getGradientId = (color: string): string => {
    return `heatmap-gradient-${color.replace('#', '')}`;
  };

  return (
    <g id="heatmap-plot">
      <defs>
        {/* Create radial gradients for each color */}
        {Array.from(eventsByColor.keys()).map((color) => {
          const gradientId = getGradientId(color);
          
          return (
            <radialGradient key={gradientId} id={gradientId} cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor={color} stopOpacity={opacity} />
              <stop offset="40%" stopColor={color} stopOpacity={opacity * 0.8} />
              <stop offset="70%" stopColor={color} stopOpacity={opacity * 0.4} />
              <stop offset="100%" stopColor={color} stopOpacity="0" />
            </radialGradient>
          );
        })}
        
        {/* Gaussian blur filter for smooth blending */}
        <filter id="heatmap-blur" x="-100%" y="-100%" width="300%" height="300%">
          <feGaussianBlur in="SourceGraphic" stdDeviation={smoothness} />
        </filter>
        
        {/* Composite filter for better blending */}
        <filter id="heatmap-composite" x="-100%" y="-100%" width="300%" height="300%">
          <feGaussianBlur in="SourceGraphic" stdDeviation={smoothness} result="blur" />
          <feColorMatrix
            in="blur"
            type="matrix"
            values="1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  0 0 0 1.2 0"
            result="enhanced"
          />
        </filter>
      </defs>
      
      {/* Render heatmap circles for each color group */}
      {Array.from(eventsByColor.entries()).map(([color, points]) => {
        const gradientId = getGradientId(color);
        
        return (
          <g key={color} filter="url(#heatmap-blur)">
            {points.map((point, index) => (
              <circle
                key={`${color}-${index}`}
                cx={point.x}
                cy={point.y}
                r={radius}
                fill={`url(#${gradientId})`}
                opacity={1}
              />
            ))}
          </g>
        );
      })}
    </g>
  );
};

export default HeatmapPlot;
